import * as v from 'valibot'
import { PAY_RATE_TYPES, type PayRateType } from './jobs'

/**
 * Payment sheets: the month's pay, kept where the work is. One sheet per
 * period, one row per player, one column per job, built from the hours the
 * players logged. Money is whole minor units, like the ledger.
 *
 * The player's row moves through four states — unpaid, on hold, paid,
 * cancelled — and once the sheet is shared, the player reads their own row
 * as a statement and accepts or disputes it.
 */

export const PAYOUT_STATUSES = ['unpaid', 'on_hold', 'paid', 'cancelled'] as const
export type PayoutStatus = (typeof PAYOUT_STATUSES)[number]

export const PAYOUT_STATUS_LABEL: Record<PayoutStatus, string> = {
  unpaid: 'Unpaid',
  on_hold: 'On hold',
  paid: 'Paid',
  cancelled: 'Cancelled',
}

export const STATEMENT_REVIEWS = ['pending', 'accepted', 'disputed'] as const
export type StatementReview = (typeof STATEMENT_REVIEWS)[number]

export const CLIENT_BILLING_STATUSES = ['unbilled', 'invoiced', 'received'] as const
export type ClientBillingStatus = (typeof CLIENT_BILLING_STATUSES)[number]

export const CLIENT_BILLING_LABEL: Record<ClientBillingStatus, string> = {
  unbilled: 'Not billed',
  invoiced: 'Invoiced',
  received: 'Received',
}

/** One change to an assignment's hours, as the hours log recorded it. */
export interface HourEntry {
  at: string
  hours: number
}

/** A column: one job, the rate it pays on this sheet, and what the client is charged. */
export interface PaymentSheetTask {
  jobId: string
  jobTitle: string
  /** The client's id, or '' for internal work — the key the client tabs group by. */
  clientKey: string
  clientName: string
  /** The rate this sheet pays; null until the job or the sheet names one. */
  rateType: PayRateType | null
  rateAmount: number | null
  /** The rate came from the sheet, not the job. */
  rateOnSheet: boolean
  clientRateType: PayRateType | null
  clientRateAmount: number | null
}

/** A cell: one player's hours on one job, and what they come to. */
export interface PaymentSheetLine {
  assignmentId: string
  jobId: string
  playerId: string
  hours: number
  /** Null while the job has no rate — never a guessed number. */
  amount: number | null
  entries: HourEntry[]
}

/** A row: one player's month. */
export interface PaymentSheetPlayer {
  playerId: string
  name: string
  pictureUrl: string | null
  extraAmount: number
  extraReason: string | null
  method: string | null
  account: string | null
  status: PayoutStatus
  statusAt: string | null
  review: StatementReview
  reviewAt: string | null
  dispute: string | null
}

export interface PaymentSheetClient {
  key: string
  name: string
  status: ClientBillingStatus
}

export interface PaymentSheet {
  id: string
  title: string
  periodStart: string
  periodEnd: string
  sharedAt: string | null
  createdAt: string
  updatedAt: string
  tasks: PaymentSheetTask[]
  players: PaymentSheetPlayer[]
  lines: PaymentSheetLine[]
  clients: PaymentSheetClient[]
}

export interface PaymentSheetSummary {
  id: string
  title: string
  periodStart: string
  periodEnd: string
  sharedAt: string | null
  createdAt: string
  players: number
  paid: number
  disputed: number
  hours: number
}

/** One line of a player's statement: a job they worked, and what it paid. */
export interface StatementLine {
  jobId: string
  jobTitle: string
  clientName: string | null
  hours: number
  rateType: PayRateType | null
  rateAmount: number | null
  amount: number | null
  entries: HourEntry[]
}

/** A player's own row of a shared sheet — nothing of anyone else's. */
export interface PlayerStatement {
  sheetId: string
  title: string
  periodStart: string
  periodEnd: string
  sharedAt: string
  lines: StatementLine[]
  extraAmount: number
  extraReason: string | null
  method: string | null
  account: string | null
  status: PayoutStatus
  statusAt: string | null
  review: StatementReview
  reviewAt: string | null
  dispute: string | null
}

const day = v.pipe(v.string(), v.regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-10-01.'))
const money = v.pipe(v.number(), v.integer('Money is whole minor units.'), v.minValue(0), v.maxValue(100_000_000))
const shortText = (max: number) => v.union([v.pipe(v.string(), v.trim(), v.maxLength(max)), v.null()])
const nonEmpty = <T extends object>(input: T) => Object.values(input).some((value) => value !== undefined)

export const createPaymentSheetSchema = v.pipe(
  v.object({
    title: v.optional(v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(80))),
    periodStart: day,
    periodEnd: day,
  }),
  v.check((input) => input.periodStart <= input.periodEnd, 'The period ends before it starts.'),
)

export const updateSheetPlayerSchema = v.pipe(
  v.object({
    extraAmount: v.optional(money),
    extraReason: v.optional(shortText(300)),
    method: v.optional(shortText(60)),
    account: v.optional(shortText(120)),
    status: v.optional(v.picklist(PAYOUT_STATUSES)),
  }),
  v.check(nonEmpty, 'Nothing to change.'),
)

export const updateSheetTaskSchema = v.object({
  rateType: v.picklist(PAY_RATE_TYPES),
  rateAmount: v.union([v.pipe(money, v.minValue(1, 'A rate of nothing is not a rate.')), v.null()]),
})

export const updateSheetClientSchema = v.object({ status: v.picklist(CLIENT_BILLING_STATUSES) })

export const disputeStatementSchema = v.object({
  reason: v.pipe(v.string(), v.trim(), v.minLength(3, 'Say what is wrong, in at least a few letters.'), v.maxLength(4000)),
})

export type CreatePaymentSheetInput = v.InferOutput<typeof createPaymentSheetSchema>
export type UpdateSheetPlayerInput = v.InferOutput<typeof updateSheetPlayerSchema>
export type UpdateSheetTaskInput = v.InferOutput<typeof updateSheetTaskSchema>
export type UpdateSheetClientInput = v.InferOutput<typeof updateSheetClientSchema>
export type DisputeStatementInput = v.InferOutput<typeof disputeStatementSchema>
